// AquaFlow: drawn network -> EPANET model (epanet-js, offline WASM) -> pressures, flows, supply zones
import { pipeLatLngs } from './model';
import { catItem } from './catalog';
import { isRot, armState } from './fitSymbols';

const M_PER_DEG = 111320;
const BAR_M = 10.197;
const num = (v) => (v === '' || v == null || !Number.isFinite(+v) ? null : +v);
const dist = (a, b) => Math.hypot((a[0] - b[0]) * M_PER_DEG, (a[1] - b[1]) * M_PER_DEG * Math.cos((a[0] * Math.PI) / 180));
const ck = (p) => `c${p[0].toFixed(6)}_${p[1].toFixed(6)}`;
export const VALVE_T = ['valve', 'prv', 'pump'];
// map shows the scenario valve position (with the real one in the tooltip)
export const scenFits = (p) => { const sv = p.aqua?.scen?.valves || {}; return Object.keys(sv).length ? (p.fittings || []).map((f) => (sv[f.id] && sv[f.id] !== f.state ? { ...f, state: sv[f.id], scenReal: f.state || 'unknown' } : f)) : p.fittings; };
// storage objects from the catalog (tank, reservoir, cistern, well) feed the network unless unticked
const TANK = /behälter|speicher|reservoir|zisterne|brunnen|quelle|tank|serbatoio|cistern/i;
export const isTank = (f) => TANK.test(`${catItem(f.catId)?.name || ''} ${f.name || ''}`);
export const isSource = (f) => !!f && !VALVE_T.includes(f.type) && (f.type === 'source' || f.hyd?.src === true || (f.hyd?.src !== false && isTank(f)));

// inner diameter in mm from "DN 150", "110x10", "PE 110" …; plastics are given as outer diameter (SDR 11)
export function innerD(p) {
  const o = num(p.hyd?.d);
  if (o) return o;
  const n = num(String(p.dn || '').match(/\d+(\.\d+)?/)?.[0]);
  if (!n) return 100;
  return /pe|pvc|pp/i.test(`${p.material || ''} ${p.dn || ''}`) && !/dn/i.test(String(p.dn)) ? Math.round(n * 0.82) : n;
}
export function hwC(p) {
  const c = num(p.hyd?.c);
  if (c) return c;
  const m = String(p.material || p.cat?.name || '').toLowerCase();
  return /pe|pvc|pp|kunst/.test(m) ? 145 : /gg|guss|cast|gus/.test(m) ? 100 : /stahl|steel|st\b/.test(m) ? 120 : /az|asbest|faser/.test(m) ? 130 : 130;
}
// Darcy-Weisbach operating roughness k in mm (DVGW GW 303-1) by pipe role, raised by material ageing
export const ROLE_K = { trunk: 0.1, main: 0.4, dist: 1.0, service: 0.4 };
export const matOf = (p) => { const m = `${p.material || ''} ${p.cat?.name || ''}`.toLowerCase(); return /ggg|duktil|ductile|sphäro/.test(m) ? 'ggg' : /\bgg\b|grauguss|grey|gray|cast/.test(m) ? 'gg' : /stahl|steel|\bst\b/.test(m) ? 'st' : /\baz\b|asbest|faser/.test(m) ? 'az' : /pe|pvc|pp|kunst/.test(m) ? 'pl' : ''; };
export function pipeRole(p, byId) {
  if (p.hyd?.role) return p.hyd.role;
  if (p.type === 'house' || (p.points || []).some((x) => byId?.[x?.fid]?.type === 'house')) return 'service';
  const n = String(p.name || ''), d = innerD(p);
  if (/zubring|fernl|transport|druckleitung|saug|stutzen/i.test(n) || d >= 250) return 'trunk';
  if (/haupt|main/i.test(n) || d >= 150) return 'main';
  return 'dist';
}
// wall roughness from age: unlined grey cast iron / steel incrust, lined ductile iron and AC age slowly, plastics stay smooth
export function kAge(p) {
  const y = num(p.hyd?.year); if (!y) return null;
  const a = Math.max(0, new Date().getFullYear() - y), m = matOf(p);
  return m === 'gg' ? Math.min(4, 0.5 + 0.06 * a) : m === 'st' ? Math.min(3, 0.15 + 0.04 * a) : m === 'ggg' || m === 'az' ? Math.min(1, 0.1 + 0.01 * a) : m === 'pl' ? 0.01 : null;
}
const cToK = (c) => (c >= 140 ? 0.01 : c >= 130 ? 0.1 : c >= 120 ? 0.4 : c >= 110 ? 1 : c >= 100 ? 1.5 : c >= 90 ? 2.5 : 4);
export const kDefault = (p, byId) => +Math.max(ROLE_K[pipeRole(p, byId)] ?? 1, kAge(p) ?? 0).toFixed(3);
export function kOf(p, byId) { const k = num(p.hyd?.k); if (k != null) return k; const c = num(p.hyd?.c); return c ? cToK(c) : kDefault(p, byId); }
// imported EPANET files with C values (and the reference test) keep Hazen-Williams, everything else Darcy-Weisbach
export const isHW = (project) => project.aqua?.hl === 'HW' || (project.aqua?.hl !== 'DW' && (project.pipes || []).length > 0 && (project.pipes || []).every((p) => num(p.hyd?.c) && num(p.hyd?.k) == null));
export const depthOf = (p, f) => num(f?.depth) ?? num(p?.hyd?.depth) ?? num(p?.depth) ?? 1.5;

// polyline cut between chainage s0..s1
function cut(pts, cum, s0, s1) {
  const at = (s) => { let i = 1; while (i < cum.length - 1 && cum[i] < s) i += 1; const d = cum[i] - cum[i - 1] || 1, k = Math.max(0, Math.min(1, (s - cum[i - 1]) / d)); return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * k, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k]; };
  return [at(s0), ...pts.filter((_, i) => cum[i] > s0 && cum[i] < s1), at(s1)];
}
function chain(pts, q) {
  let best = { d: Infinity, s: 0 }, s = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i], L = dist(a, b), dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy || 1;
    const u = Math.max(0, Math.min(1, ((q[0] - a[0]) * dx + (q[1] - a[1]) * dy) / l2)), p = [a[0] + u * dx, a[1] + u * dy], d = dist(p, q);
    if (d < best.d) best = { d, s: s + u * L };
    s += L;
  }
  return best.s;
}

// network graph: nodes (fittings + free pipe ends), links (pipe pieces, valve links)
export function buildNet(project, opt = {}) {
  // drafts (placed with low GPS precision) are part of the network; they are only listed as a hint
  const fits = (project.fittings || []).filter((f) => f.type !== 'leak' || isLeak(f)), byId = Object.fromEntries(fits.map((f) => [f.id, f]));
  // scenario valve positions never touch the documented real state (f.state)
  const sv = opt.valves || {}, posOf = (f) => sv[f.id] || f.state || 'unknown';
  const closed = opt.closed || new Set(fits.filter((f) => f.type === 'valve' && posOf(f) === 'closed').map((f) => f.id));
  const q = { dEst: [], dDef: [], cDef: [], hydSmall: [], svcSmall: [], distSmall: [], depthDef: [], demDef: [], srcDef: [], zMiss: [], zDem: [], drafts: fits.filter((f) => f.draft).map((f) => f.id), openEnds: 0, unknownValves: fits.filter((f) => f.type === 'valve' && !sv[f.id] && !['open', 'closed', 'partial'].includes(f.state)).map((f) => f.id) };
  const nodes = {}, links = [], side = {};
  const node = (id, ll, extra) => { if (!nodes[id]) nodes[id] = { id, lat: ll[0], lon: ll[1], ...extra }; return id; };
  // valves split into two ends a/b, the first pipe end that reaches it takes side a
  const endOf = (f, prefer) => {
    if (!VALVE_T.includes(f.type)) return node(f.id, [f.lat, f.lon], { fid: f.id });
    node(f.id, [f.lat, f.lon], { fid: f.id }); node(`${f.id}~b`, [f.lat, f.lon], { fid: f.id, twin: true });
    const s = prefer || (side[f.id] ? 'b' : 'a'); side[f.id] = true;
    return s === 'a' ? f.id : `${f.id}~b`;
  };
  (project.pipes || []).forEach((p) => {
    if (p.plan === 'removed' || p.status === 'abandoned') return;
    const pts = pipeLatLngs(p, fits);
    if (pts.length < 2 || pts.some((x) => !x || !Number.isFinite(x[0]))) return;
    const cum = pts.reduce((a, q, i) => [...a, i ? a[i - 1] + dist(pts[i - 1], q) : 0], []), L = cum[cum.length - 1];
    if (L < 0.05) return;
    const st = [];
    p.points.forEach((pt, i) => {
      if (pt?.fid && byId[pt.fid]) st.push({ f: byId[pt.fid], s: cum[i], end: i === 0 || i === p.points.length - 1 });
      else if (i === 0 || i === p.points.length - 1) { q.openEnds += 1; st.push({ key: `${p.id}@${i}`, ll: pts[i], s: cum[i] }); }
    });
    fits.filter((f) => f.hostPipe === p.id && !st.some((x) => x.f?.id === f.id)).forEach((f) => st.push({ f, s: chain(pts, [f.lat, f.lon]), host: true }));
    st.sort((a, b) => a.s - b.s);
    // imported networks (EPANET demo) keep their true pipe length in hyd.len
    const dI = innerD(p), cI = hwC(p), kI = kOf(p, byId), dpI = depthOf(p), sc = num(p.hyd?.len) ? num(p.hyd.len) / L : 1;
    if (num(p.hyd?.d) == null) q[num(String(p.dn || '').match(/\d+/)?.[0]) ? 'dEst' : 'dDef'].push(p.id);
    if (num(p.hyd?.c) == null && num(p.hyd?.k) == null) q.cDef.push(p.id);
    if (num(p.hyd?.depth) == null && num(p.depth) == null) q.depthDef.push(p.id);
    // dimension check (DVGW W 400-1: supply and hydrant lines ≥ DN 80; W 406: service line not smaller than the meter)
    const ends = p.points.map((x) => byId[x?.fid]).filter(Boolean), dIn = innerD(p);
    const meter = ends.map((f) => (f.type === 'house' ? num(f.meter?.dn) ?? num(String(f.note || '').match(/DN\s*(\d+)/)?.[1]) : null)).find((v) => v != null);
    if (ends.some((f) => f.type === 'hydrant') && dIn < 70) q.hydSmall.push(p.id);
    else if (meter != null && dIn < meter * 1.2) q.svcSmall.push(p.id);
    else if (!ends.some((f) => f.type === 'house') && pipeRole(p, byId) === 'dist' && dIn < 70) q.distSmall.push(p.id);
    for (let i = 1; i < st.length; i++) {
      const A = st[i - 1], B = st[i], len = Math.max(0.5, (B.s - A.s) * sc);
      const na = A.f ? endOf(A.f, A.host ? 'b' : null) : node(A.key, A.ll, {});
      const nb = B.f ? endOf(B.f, B.host ? 'a' : null) : node(B.key, B.ll, {});
      if (na === nb) continue;
      // valve cross / tee: a closed valve on the arm towards this pipe piece shuts the piece
      const shut = (A.f && isRot(A.f) && armState(A.f, `${p.id}>`) === 'closed') || (B.f && isRot(B.f) && armState(B.f, `${p.id}<`) === 'closed');
      links.push({ id: `L${links.length + 1}`, kind: 'pipe', pipeId: p.id, a: na, b: nb, len, d: dI, c: cI, k: kI, depth: dpI, coords: cut(pts, cum, A.s, B.s), ...(shut ? { closed: true, armShut: true } : {}) });
    }
  });
  // valve links between the two ends
  fits.filter((f) => VALVE_T.includes(f.type) && nodes[f.id]).forEach((f) => {
    const host = links.find((l) => l.a === f.id || l.b === f.id || l.a === `${f.id}~b` || l.b === `${f.id}~b`);
    const kind = f.type === 'valve' ? 'valve' : f.type;
    links.push({ id: `V_${f.id}`, kind, fid: f.id, a: f.id, b: `${f.id}~b`, len: 1, d: host?.d || 100, c: 130, k: 0.1, depth: host?.depth ?? 1.5, closed: (f.type === 'valve' && closed.has(f.id)) || (kind === 'pump' && f.hyd?.ctl?.mode === 'off'), set: num(f.hyd?.set) ?? 3, coords: [[f.lat, f.lon], [f.lat, f.lon]] });
  });
  // supply: reachable from a source over open links (PRV direction: upstream side becomes a)
  const srcIds = Object.values(nodes).filter((n) => n.fid && !n.twin && isSource(byId[n.fid])).map((n) => n.id);
  const adj = {}; links.forEach((l) => { if (l.closed) return; (adj[l.a] ||= []).push([l.b, l]); (adj[l.b] ||= []).push([l.a, l]); });
  const seen = new Set(srcIds), bfs = [...srcIds];
  while (bfs.length) { const id = bfs.shift(); (adj[id] || []).forEach(([o, l]) => { if (!seen.has(o)) { if ((l.kind === 'prv' || l.kind === 'pump') && o === l.a) { l.a = l.b; l.b = o; } seen.add(o); bfs.push(o); } }); }
  // pump: suction side = side reached first from the source; 'rev' flips it
  // imported links keep the EPANET direction (hyd.from = fitting on the suction / upstream side)
  links.filter((l) => (l.kind === 'pump' || l.kind === 'prv') && byId[l.fid]?.hyd?.from).forEach((l) => {
    const fr = byId[l.fid].hyd.from, k = links.find((x) => x.kind === 'pipe' && ((x.a === fr && [l.a, l.b].includes(x.b)) || (x.b === fr && [l.a, l.b].includes(x.a))));
    const s = k && (k.a === fr ? k.b : k.a); if (s && l.a !== s) [l.a, l.b] = [l.b, l.a];
  });
  links.filter((l) => l.kind === 'pump' && byId[l.fid]?.hyd?.rev).forEach((l) => { [l.a, l.b] = [l.b, l.a]; });
  Object.values(nodes).forEach((n) => { n.supplied = seen.has(n.id); });
  return { nodes, links, srcIds, byId, closed, q, posOf };
}

// elevations (pipe level = ground - laying depth): measured fitting height, else terrain model (online, cached)
const DEMC = (() => { try { return JSON.parse(localStorage.getItem('giss_aq_dem')) || {}; } catch { return {}; } })();
async function demBatch(list) {
  const miss = list.filter((n) => DEMC[ck([n.lat, n.lon])] == null);
  for (let i = 0; i < miss.length && navigator.onLine; i += 90) {
    const part = miss.slice(i, i + 90);
    try {
      const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${part.map((n) => n.lat.toFixed(5)).join(',')}&longitude=${part.map((n) => n.lon.toFixed(5)).join(',')}`);
      const el = (await r.json()).elevation || [];
      part.forEach((n, k) => { if (Number.isFinite(+el[k])) DEMC[ck([n.lat, n.lon])] = +el[k]; });
    } catch { break; }
  }
  try { localStorage.setItem('giss_aq_dem', JSON.stringify(DEMC)); } catch { /* full */ }
}
export async function withElevation(net) {
  const list = Object.values(net.nodes);
  await demBatch(list.filter((n) => num(net.byId[n.fid]?.elev?.v) == null));
  const miss = [];
  list.forEach((n) => {
    const f = net.byId[n.fid], lk = net.links.find((l) => l.kind === 'pipe' && (l.a === n.id || l.b === n.id));
    const g = num(f?.elev?.v) ?? DEMC[ck([n.lat, n.lon])];
    if (!n.twin && g == null) miss.push(n.fid || n.id); else if (!n.twin && num(f?.elev?.v) == null && n.fid) net.q.zDem.push(n.fid);
    n.ground = g ?? 0;
    n.z = n.ground - (num(f?.depth) ?? lk?.depth ?? 1.5);
  });
  return miss;
}

const fx = (v, d = 3) => (+v).toFixed(d);
let worker = null, seq = 0;
const pending = new Map();
const getWorker = () => {
  if (!worker) {
    worker = new Worker(new URL('./aquaWorker.js', import.meta.url));
    worker.onmessage = ({ data }) => { if (data.ver) window.__gissEpanetVer = data.ver; const p = pending.get(data.id); pending.delete(data.id); p?.(data); };
  }
  return worker;
};
// stop a running calculation (terminates the background solver)
export function cancelSim() {
  if (worker) { worker.terminate(); worker = null; }
  pending.forEach((p) => p({ error: 'cancel' })); pending.clear();
}
const solve = (inp, nodes, links) => new Promise((done) => { const id = ++seq; pending.set(id, done); getWorker().postMessage({ id, inp, nodes, links }); });
export const solveRef = (inp) => new Promise((done) => { const id = ++seq; pending.set(id, done); getWorker().postMessage({ id, inp, ref: true }); });
const solveEps = (inp, nodes, links, tanks, hours) => new Promise((done) => { const id = ++seq; pending.set(id, done); getWorker().postMessage({ id, inp, nodes, links, tanks, hours, eps: true }); });

// demand of a node: entered value, otherwise house connection default (listed as assumption)
const demandOf = (f, q) => {
  if (!f) return 0;
  const v = num(f.hyd?.q);
  if (v != null) return v;
  if (f.type === 'house') { q.demDef.push(f.id); return 0.05; }
  return 0;
};

// storage tank: water level, limits and size instead of a fixed pressure (incomplete data -> fixed pressure, flagged)
function tankOf(f, n, q) {
  const h = f?.hyd || {};
  if (h.mode !== 'tank') return null;
  const lvl = num(h.level), max = num(h.max), vol = num(h.vol), dm = num(h.diam);
  if (lvl == null || max == null || (!vol && !dm)) { q.tankMiss.push(f.id); return null; }
  return { elev: n.ground + (num(h.base) ?? 0), lvl, min: num(h.min) ?? 0, max, diam: dm || Math.sqrt((4 * vol) / (Math.PI * max)) };
}
// typical residential day profile (assumption, mean = 1), hour 0..23
const PROFILE = [0.4, 0.3, 0.25, 0.25, 0.3, 0.5, 1.1, 1.6, 1.5, 1.3, 1.2, 1.15, 1.2, 1.1, 1, 1, 1.05, 1.2, 1.4, 1.5, 1.3, 1.1, 0.8, 0.55];
export const PROF = PROFILE.map((x) => x / (PROFILE.reduce((s, v) => s + v, 0) / 24));
// own day profile of the project (24 hourly values from meter data, any unit) normalised to mean 1, else the default
const norm24 = (arr) => { const c = (arr || []).map(Number), m = c.reduce((x, v) => x + v, 0) / 24; return c.length === 24 && c.every((v) => Number.isFinite(v) && v >= 0) && m > 0 ? c.map((v) => v / m) : null; };
// typical weekend profile (later, flatter morning peak), mean = 1
const PROFILE_WE = [0.5, 0.4, 0.3, 0.25, 0.25, 0.3, 0.45, 0.75, 1.15, 1.45, 1.5, 1.4, 1.35, 1.2, 1.05, 1, 1.05, 1.15, 1.3, 1.35, 1.25, 1.1, 0.9, 0.7];
export const PROF_WE = PROFILE_WE.map((x) => x / (PROFILE_WE.reduce((s, v) => s + v, 0) / 24));
export const profOf = (cfg, day) => (day === 'we' ? norm24(cfg?.profWe) || PROF_WE : norm24(cfg?.prof) || PROF);

// pumps: [PUMPS] + [CURVES] (1- or 3-point, l/s and m) + [STATUS] + [CONTROLS] (tank level / clock time)
const hhmm = (s) => (/^\d{1,2}:\d{2}$/.test(s || '') ? s : null);
function pumpInp(L, net, tk, q) {
  const P = L.filter((l) => l.kind === 'pump'), cur = [], pu = [], ctl = [], st = [];
  q.pumpDef = [];
  P.forEach((l) => {
    const h = net.byId[l.fid]?.hyd || {}, q1 = num(h.q1), h1 = num(h.h1), h0 = num(h.h0), q2 = num(h.q2), h2 = num(h.h2), c = h.ctl || {};
    if (!(q1 > 0 && h1 > 0)) q.pumpDef.push(l.fid);
    const pts = h0 > 0 && q2 > 0 && h2 > 0 ? [[0, h0], [q1 || 10, h1 || 40], [q2, h2]] : [[q1 || 10, h1 || 40]];
    cur.push(...pts.map(([x, y]) => `C_${l.id} ${fx(x, 3)} ${fx(y, 2)}`));
    pu.push(`${l.id} ${l.a} ${l.b} HEAD C_${l.id} SPEED ${fx(num(h.speed) || 1, 3)}`);
    const tank = c.tank && Object.keys(tk).find((id) => net.nodes[id]?.fid === c.tank);
    if (c.mode === 'level' && tank && num(c.on) != null && num(c.off) != null) ctl.push(`LINK ${l.id} OPEN IF NODE ${tank} BELOW ${fx(num(c.on), 2)}`, `LINK ${l.id} CLOSED IF NODE ${tank} ABOVE ${fx(num(c.off), 2)}`);
    else if (c.mode === 'time' && hhmm(c.ton) && hhmm(c.toff)) {
      ctl.push(`LINK ${l.id} OPEN AT CLOCKTIME ${c.ton}`, `LINK ${l.id} CLOSED AT CLOCKTIME ${c.toff}`);
      // simulation starts at 00:00: off at midnight unless the on-window spans it
      if (!(c.ton > c.toff)) st.push(`${l.id} CLOSED`);
    }
    else if (c.mode === 'level' || c.mode === 'time') q.pumpDef.push(l.fid);
  });
  return P.length ? ['[PUMPS]', ...pu, '[CURVES]', ...cur, '[STATUS]', ...st, '[CONTROLS]', ...ctl] : [];
}

// simulated leak: active leak marker on a pipe; fixed outflow (l/s) or pressure-dependent hole (EPANET emitter)
export const isLeak = (f) => f.type === 'leak' && !!f.hyd?.leakOn && !!f.hostPipe;
const leakFixed = (f) => (isLeak(f) && f.hyd.lmode !== 'hole' ? num(f.hyd.lq) ?? 0 : 0);
// Q = Cd · A · √(2 g h) -> emitter coefficient in l/s per m^0.5 (Cd 0.6)
export const leakCoef = (f) => { const d = (num(f.hyd?.ld) ?? 5) / 1000; return 0.6 * ((Math.PI * d * d) / 4) * Math.sqrt(2 * 9.81) * 1000; };

// run one steady-state hydraulic analysis (or an extended period with water age: opt.eps = { hours, profile })

export async function simulate(project, cfg = {}, opt = {}) {
  const net = opt.net || buildNet(project, { valves: cfg.scen?.valves, ...opt });
  const q = net.q;
  if (!net.srcIds.length) return { error: 'noSource', quality: q };
  q.zMiss = opt.net ? [] : await withElevation(net);
  q.tankMiss = [];
  const live = Object.values(net.nodes).filter((n) => n.supplied), ok = new Set(live.map((n) => n.id));
  const L = net.links.filter((l) => ok.has(l.a) && ok.has(l.b) && !l.closed);
  const dF = num(cfg.demandF) ?? 1;
  const dem = (n) => { if (n.twin) return 0; if (opt.extra?.[n.id]) return opt.extra[n.id] + dem({ ...n, id: null }); const f = net.byId[n.fid]; if (f?.type === 'leak') return leakFixed(f); if (opt.leakOnly) return 0; let v = demandOf(f, q) * dF; if (cfg.fire?.id && cfg.fire.id === n.fid) v += num(cfg.fire.q) || 0; return v + (f ? hydQ(f, opt) : 0); };
  const srcBar = (n) => { const v = num(net.byId[n.fid]?.hyd?.bar); if (v == null) q.srcDef.push(n.fid); return v ?? 4; };
  const J = live.filter((n) => !net.srcIds.includes(n.id)), S = live.filter((n) => net.srcIds.includes(n.id));
  // everything cut off from the sources (e.g. the feeding cross arm closed): nothing to solve
  if (!J.length && !opt.eps) {
    const dry = Object.values(net.nodes).filter((n) => !n.supplied && n.fid && !n.twin);
    return { nodes: Object.fromEntries(S.map((n) => [n.id, { ...n, bar: srcBar(n), head: 0, demand: 0 }])), links: net.links.filter((l) => l.kind === 'pipe').map((l) => ({ ...l, q: 0, v: 0, dead: true })), inflow: 0, unsupplied: dry.map((n) => n.fid), dry: dry.map((n) => [n.lat, n.lon]), srcIds: net.srcIds, quality: q, at: Date.now(), scenario: !!Object.keys(cfg.scen?.valves || {}).length, key: JSON.stringify([cfg.scen?.valves || {}, cfg.demandF, cfg.fire]), pumps: [], min: null, max: null };
  }
  const tk = Object.fromEntries(S.map((n) => [n.id, tankOf(net.byId[n.fid], n, q)]).filter(([, v]) => v));
  const R = S.filter((n) => !tk[n.id]), E = opt.eps, pat = E ? ' P1' : '', hw = isHW(project);
  const inp = ['[TITLE]', 'AquaFlow', '[JUNCTIONS]', ...J.map((n) => `${n.id} ${fx(n.z)} ${fx(dem(n), 4)}${pat}`),
    '[RESERVOIRS]', ...R.map((n) => `${n.id} ${fx(n.z + srcBar(n) * BAR_M)}`),
    '[EMITTERS]', ...J.filter((n) => isLeak(net.byId[n.fid] || {}) && net.byId[n.fid].hyd.lmode === 'hole').map((n) => `${n.id} ${fx(leakCoef(net.byId[n.fid]), 5)}`),
    '[TANKS]', ...Object.entries(tk).map(([id, v]) => `${id} ${fx(v.elev)} ${fx(v.lvl)} ${fx(v.min)} ${fx(v.max)} ${fx(v.diam)} 0`),
    ...(E ? ['[PATTERNS]', `P1 ${(E.profile === 'flat' ? PROF.map(() => 1) : profOf(cfg)).map((x) => x.toFixed(3)).join(' ')}`,
      '[TIMES]', `Duration ${E.hours}:00`, 'Hydraulic Timestep 1:00', 'Quality Timestep 0:05', 'Pattern Timestep 1:00', 'Report Timestep 1:00'] : []),
    ...pumpInp(L, net, tk, q),
    '[PIPES]', ...L.filter((l) => l.kind !== 'prv' && l.kind !== 'pump').map((l) => `${l.id} ${l.a} ${l.b} ${fx(l.len, 2)} ${fx(l.d, 1)} ${hw ? l.c : fx(l.k, 4)} 0 Open`),
    '[VALVES]', ...L.filter((l) => l.kind === 'prv').map((l) => `${l.id} ${l.a} ${l.b} ${fx(l.d, 1)} PRV ${fx(l.set * BAR_M, 2)} 0`),
    '[OPTIONS]', 'Units LPS', `Headloss ${hw ? 'H-W' : 'D-W'}`, 'Demand Model PDA', 'Minimum Pressure 0', 'Required Pressure 1', 'Pressure Exponent 0.5', 'Viscosity 1.3', 'Specific Gravity 1', 'Unbalanced Continue 10', ...(E ? ['Quality Age'] : []), '[END]'].join('\n');
  if (E) {
    const r = await solveEps(inp, live.map((n) => n.id), L.map((l) => l.id), Object.keys(tk), E.hours);
    if (r.error) return { error: r.error === 'cancel' ? 'cancel' : 'calc', detail: r.error === 'cancel' ? '' : r.error, quality: q };
    const e = r.eps, age = {}, ageMax = {}, pMin = {};
    live.forEach((n, k) => { age[n.id] = e.age[k][0]; ageMax[n.id] = e.age[k][1]; pMin[n.id] = e.pr[k][0] / BAR_M; });
    const tanks = Object.keys(tk).map((id, k) => { const v = tk[id], L2 = e.lvl[k], f = net.byId[net.nodes[id].fid], vol = (Math.PI * v.diam ** 2) / 4;
      const out = L.filter((l) => l.a === id || l.b === id).reduce((s, l, j) => s + Math.abs(e.q[L.indexOf(l)][0]), 0) * 86.4;
      return { id, fid: f?.id, min: Math.min(...L2), max: Math.max(...L2), lim: [v.min, v.max], volM3: vol * v.max, turnoverH: out > 0 ? (vol * ((Math.min(...L2) + Math.max(...L2)) / 2) / out) * 24 : Infinity }; });
    return { eps: { age, ageMax, pMin, tanks, hours: E.hours, profile: E.profile || 'res', samples: e.n, linkQ: Object.fromEntries(L.map((l, k) => [l.id, e.q[k][0]])) }, quality: q, ver: r.ver, at: Date.now() };
  }
  const r = await solve(inp, live.map((n) => n.id), L.map((l) => l.id));
  if (r.error) return { error: r.error === 'cancel' ? 'cancel' : 'calc', detail: r.error === 'cancel' ? '' : r.error, quality: q };
  const res = { nodes: {}, links: [], inflow: 0, unsupplied: [], dry: [], srcIds: net.srcIds, quality: q, ver: r.ver, at: Date.now(), scenario: !!Object.keys(cfg.scen?.valves || {}).length, key: JSON.stringify([cfg.scen?.valves || {}, cfg.demandF, cfg.fire]) };
  live.forEach((n, k) => {
    const [p, head, d] = r.nodes[k];
    // a reservoir reports 0 pressure in EPANET: its pressure is head minus pipe level
    res.nodes[n.id] = { ...n, bar: (net.srcIds.includes(n.id) ? head - n.z : p) / BAR_M, head, demand: d };
  });
  L.forEach((l, k) => res.links.push({ ...l, q: r.links[k][0], v: r.links[k][1] }));
  res.inflow = -S.reduce((s, n) => s + res.nodes[n.id].demand, 0);
  // nodes cut off behind a closed pump/valve give meaningless EPANET pressures -> unsupplied
  // a junction above the hydraulic grade (e.g. hydrant higher than the tank water level, no pump) gets no water at all
  res.noWater = [];
  Object.values(res.nodes).forEach((n) => {
    if (n.twin || !n.fid || net.srcIds.includes(n.id)) return;
    if (!(n.bar > -20)) { res.unsupplied.push(n.fid); res.dry.push([n.lat, n.lon]); n.bar = NaN; return; }
    if (n.bar < -0.005) { n.noWater = true; n.above = -n.bar * BAR_M; n.want = dem(n); n.demand = 0; n.bar = NaN; res.noWater.push({ fid: n.fid, above: n.above, want: n.want }); res.unsupplied.push(n.fid); res.dry.push([n.lat, n.lon]); }
  });
  // active hydrant withdrawals (open hydrant / fire flow) and consumers that get only part of their demand (pressure-driven)
  res.draws = []; res.short = [];
  Object.values(res.nodes).forEach((n) => {
    const f = n.fid && net.byId[n.fid];
    if (n.twin || !f || f.type === 'leak' || net.srcIds.includes(n.id)) return;
    const want = n.noWater ? n.want : dem(n), got = n.noWater ? 0 : Math.max(0, n.demand), fire = cfg.fire?.id === f.id;
    if (!(want > 1e-6)) return;
    if (f.type === 'hydrant' && (hydQ(f, opt) > 0 || fire)) res.draws.push({ fid: f.id, want, got, fire });
    else if (!n.noWater && got < want * 0.95) res.short.push({ fid: f.id, want, got });
  });
  res.pumps = [...net.links.filter((l) => l.kind === 'pump' && l.closed).map((l) => ({ ...l, q: 0 })), ...res.links.filter((l) => l.kind === 'pump')].map((l) => ({ fid: l.fid, q: l.q, gain: (res.nodes[l.b]?.head ?? 0) - (res.nodes[l.a]?.head ?? 0), on: Math.abs(l.q) > 1e-4 }));
  Object.values(net.nodes).filter((n) => !n.supplied && n.fid && !n.twin).forEach((n) => { res.unsupplied.push(n.fid); res.dry.push([n.lat, n.lon]); });
  net.links.filter((l) => l.kind === 'pipe' && (l.armShut || !(ok.has(l.a) && ok.has(l.b)))).forEach((l) => res.links.push({ ...l, q: 0, v: 0, dead: true }));
  const bars = Object.values(res.nodes).filter((n) => !n.twin && !net.srcIds.includes(n.id) && !VALVE_T.includes(net.byId[n.fid]?.type) && Number.isFinite(n.bar)).map((n) => n.bar);
  res.min = bars.length ? Math.min(...bars) : null; res.max = bars.length ? Math.max(...bars) : null;
  ['demDef', 'srcDef'].forEach((k) => { q[k] = [...new Set(q[k])]; });
  if (!opt.noHyd && !opt.noHydRes) { res.hyd = await hydResults(project, cfg, opt, net, res); res.pumpChk = pumpCheck(net, res); }
  if (opt.leakTest) res.leakTest = opt.leakTest;
  res.leaks = Object.values(net.byId).filter(isLeak).map((f) => { const n = res.nodes[f.id]; return { fid: f.id, mode: f.hyd.lmode === 'hole' ? 'hole' : 'fixed', q: n && Number.isFinite(n.bar) ? Math.max(0, n.demand) : 0, bar: n?.bar ?? null }; });
  res.leakQ = res.leaks.reduce((s, l) => s + l.q, 0);
  return res;
}

// hydrant withdrawal: open hydrant takes its flow (default 13.3 l/s = 48 m³/h); opt.hydQ overrides (characteristic curve)
export const HYD_DEF = 13.3;
export const hydFlow = (f) => num(f.hyd?.hq) ?? HYD_DEF;
const hydQ = (f, opt) => {
  if (f.type !== 'hydrant') return 0;
  if (opt.hydQ && opt.hydQ[f.id] != null) return opt.hydQ[f.id];
  return f.hyd?.hOpen && !opt.noHyd ? hydFlow(f) : 0;
};
// hydrant with the lowest static pressure: below 1.5 bar (DVGW W 405 fire flow) -> missing head in m and what each feed would need
function pumpCheck(net, res) {
  const H = (res.hyd || []).map((h) => ({ h, n: res.nodes[h.fid] })).filter((x) => x.n && (x.n.noWater || Number.isFinite(x.h.stat)));
  if (!H.length) return null;
  const w = H.map((x) => ({ fid: x.h.fid, z: x.n.z, stat: x.n.noWater ? -x.h.above / BAR_M : x.h.stat })).sort((a, b) => a.stat - b.stat)[0];
  if (w.stat >= 1.5) return null;
  const need = (1.5 - w.stat) * BAR_M, srcs = net.srcIds.map((id) => { const f = net.byId[id], n = res.nodes[id]; if (!f || !n) return null; const tk = f.hyd?.mode === 'tank', T = tk && tankOf(f, n, { tankMiss: [] }); return { fid: id, tank: tk, top: T && Number.isFinite(T.elev + T.max) ? T.elev + T.max : null, now: tk ? n.head : n.bar, want: tk ? n.head + need : n.bar + need / BAR_M }; }).filter(Boolean);
  // fed through a PRV (its outlet head = head of the weak hydrant in the static run)? then only the set point matters
  const hz = w.z + w.stat * BAR_M, prv = (res.links || []).filter((l) => l.kind === 'prv' && Math.abs((res.nodes[l.b]?.head ?? -1e9) - hz) < 1).map((l) => ({ fid: l.fid, set: l.set, want: l.set + need / BAR_M }));
  if (prv.length) return { ...w, need, srcs: [], pumps: [], prv };
  return { ...w, need, srcs, prv: [], pumps: (res.pumps || []).map((p) => ({ fid: p.fid, gain: p.gain, want: p.gain + need })) };
}
// static pressure (all hydrants closed, no fire flow) next to the flowing pressure of this run
async function hydResults(project, cfg, opt, net, res) {
  const H = Object.values(net.byId).filter((f) => f.type === 'hydrant' && net.nodes[f.id]);
  if (!H.length) return [];
  const anyOpen = H.some((f) => f.hyd?.hOpen) || cfg.fire?.id || opt.hydQ;
  const st = anyOpen ? await simulate(project, { ...cfg, fire: null }, { ...opt, noHyd: true, hydQ: null }) : res;
  return H.map((f) => {
    const stat = st.nodes?.[f.id]?.bar, flow = res.nodes[f.id]?.bar, open = !!f.hyd?.hOpen, qq = hydQ(f, opt) + (cfg.fire?.id === f.id ? num(cfg.fire.q) || 0 : 0);
    const nw = res.nodes[f.id]?.noWater || st.nodes?.[f.id]?.noWater, above = Math.max(res.nodes[f.id]?.above || 0, st.nodes?.[f.id]?.above || 0);
    return { fid: f.id, noWater: !!nw, above, got: nw ? 0 : qq, open: qq > 0, q: qq, stat: Number.isFinite(stat) ? stat : null, flow: Number.isFinite(flow) ? flow : null, drop: Number.isFinite(stat) && Number.isFinite(flow) ? stat - flow : null, warn: qq > 0 && !nw && (!Number.isFinite(flow) || flow < 1.5), setOpen: open };
  });
}
// characteristic curve: flowing pressure at one hydrant over its withdrawal (other open hydrants keep their flow)
export async function hydCurve(project, cfg, fid) {
  const pts = [];
  for (const qq of [0, 2.5, 5, 7.5, 10, 13.3, 16.7, 20, 26.7, 33.3, 40, 53.3]) {
    const r = await simulate(project, { ...cfg, fire: null }, { hydQ: { [fid]: qq }, noHydRes: true });
    if (r.error) return { error: r.error, pts };
    const b = r.nodes?.[fid]?.bar;
    if (!Number.isFinite(b)) break;
    pts.push([qq, b]);
    if (b < 0) break;
  }
  let qMax = null;
  for (let i = 1; i < pts.length; i++) if (pts[i - 1][1] >= 1.5 && pts[i][1] < 1.5) { const [a, b] = [pts[i - 1], pts[i]]; qMax = a[0] + ((a[1] - 1.5) * (b[0] - a[0])) / (a[1] - b[1]); break; }
  if (qMax == null && pts.length && pts[pts.length - 1][1] >= 1.5) qMax = Infinity;
  return { pts, qMax };
}

// step test: close valves one after the other, simulated inflow per step vs. measured inflow
export async function stepTest(project, cfg, steps) {
  const sv = cfg.scen?.valves || {};
  const base = new Set((project.fittings || []).filter((f) => f.type === 'valve' && (sv[f.id] || f.state) === 'closed').map((f) => f.id));
  const out = [];
  for (let k = 0; k <= steps.length; k++) {
    const closed = new Set([...base, ...steps.slice(0, k).map((s) => s.vid)]);
    const r = await simulate(project, cfg, { closed });
    if (r.error) return { error: r.error, detail: r.detail };
    out.push({ k, vid: steps[k - 1]?.vid, sim: r.inflow, meas: num(steps[k - 1]?.meas ?? cfg.meas0), lost: r.unsupplied.length });
  }
  out.forEach((s, i) => { if (!i) return; const p = out[i - 1]; s.dSim = p.sim - s.sim; s.dMeas = p.meas != null && s.meas != null ? p.meas - s.meas : null; s.leak = s.dMeas != null ? s.dMeas - s.dSim : null; });
  return { steps: out };
}

export const PCOL = [[1.5, '#2563eb'], [3, '#16a34a'], [5, '#84cc16'], [6.5, '#eab308'], [8, '#f97316'], [Infinity, '#dc2626']];
export const pColor = (bar) => (!Number.isFinite(bar) ? '#94a3b8' : PCOL.find(([x]) => bar < x)[1]);
export const TCOL = [[0.5, '#7c3aed'], [5, '#2563eb'], [12, '#16a34a'], [20, '#eab308'], [25, '#f97316'], [Infinity, '#dc2626']];
export const tColor = (c) => (!Number.isFinite(c) ? '#94a3b8' : TCOL.find(([x]) => c < x)[1]);
